import { CommonModule } from '@angular/common';

import { Component, ElementRef, HostListener, OnInit, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Route, Router, RouterModule } from '@angular/router';
import { AppDatePipe } from '../../../app/helpers/app-date.pipe';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ComboboxComponent,
  ComboboxInputDirective,
  ComboboxSingleItemComponent,
  HeaderAppNameComponent,
  HeaderCenterDirective,
  HeaderComponent,
  HeaderEndDirective,
  HeaderMobileMenuActionComponent,
  HeaderMobileMenuComponent,
  HeaderMobileMenuEndDirective,
  HeaderMobileMenuStartDirective,
  HeaderNavigationLinkComponent,
  IconComponent,
  TagComponent,
} from '@totalenergiescode/tds-angular';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';

import { User } from '../../../app/administration/users/user.model';
import { UsersService } from '../../../app/administration/users/users.service';
import { applicationRouteNames } from '../../../app/application-route-names';
import { NotificationBellComponent } from '../../../app/dashboard/components/notification-bell/notification-bell.component';
import { MpsRoleHelper } from '../../../app/helpers/mps-role.helper';
import { ManagerEoService } from '../../../app/services/administration/manager-eo.service';
import { environment } from '../../../environments/environment';
import { MpsPermissionHelper } from '../../helpers/mps-permission.helper';
import { mpsRouteNames } from '../../mps-route-names';
import { MpsNotificationService } from '../../services/mps-notification.service';
import { MpsUserService } from '../../services/mps-user.service';

@Component({
  selector: 'mps-app-header',
  templateUrl: './mps-header.component.html',
  styleUrls: ['./mps-header.component.scss'],
  imports: [
    TranslateModule,
    IconComponent,
    HeaderComponent,
    HeaderAppNameComponent,
    HeaderCenterDirective,
    HeaderEndDirective,
    HeaderMobileMenuComponent,
    HeaderMobileMenuStartDirective,
    HeaderMobileMenuEndDirective,
    HeaderMobileMenuActionComponent,
    HeaderNavigationLinkComponent,
    ComboboxComponent,
    ComboboxInputDirective,
    ComboboxSingleItemComponent,
    AppDatePipe,
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    NotificationBellComponent,
    TagComponent,
  ],
})
export class MpsHeaderComponent implements OnInit {
  mpsUserService = inject(MpsUserService);
  _notificationService = inject(MpsNotificationService);
  translateService = inject(TranslateService);
  private managerEoService = inject(ManagerEoService);
  private eRef = inject(ElementRef);
  private router = inject(Router);
  user = signal(this.mpsUserService.getUserSubject().getValue());
  isDropdownOpen = signal(false);
  isUserMenuOpen = signal(false);
  logoutRoute = mpsRouteNames.auth + '/' + mpsRouteNames.logout;
  readonly canImpersonate = environment.allowImpersonation;
  private usersService = inject(UsersService);
  search = new FormControl();
  usersToImpersonate = signal<User[]>([]);
  isEnvProd = environment.production;
  envName = environment.envName;
  isMobileSubMenuOpen = signal(false);
  readonly navigationRoutes: Route[] = this.router.config;

  ngOnInit(): void {
    this.search.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter((searchInput) => searchInput.length >= 3)
      )
      .subscribe((searchInput) => {
        this.usersService.usersToImpersonate(searchInput).subscribe({
          next: (users) => {
            this.usersToImpersonate.set(users);
          },
          error: () => {
            this.usersToImpersonate.set([]);
            this._notificationService.error(this.translateService.instant('messages.api_response.generic_error'));
          },
        });
      });
  }

  getCurrentLangLabel(): string {
    return this.getLangLabel(this.translateService.currentLang);
  }

  getLangLabel(lang: string): string {
    const labels: Record<string, string> = {
      fr: 'Français',
      en: 'English',
    };
    return labels[lang] || this.translateService.instant('general.missing_translation');
  }

  toggleDropdown() {
    this.isDropdownOpen.set(!this.isDropdownOpen());
    this.isUserMenuOpen.set(false);
  }

  toggleUserMenu() {
    this.isUserMenuOpen.set(!this.isUserMenuOpen());
    this.isDropdownOpen.set(false);
  }

  switchLanguage(language: string) {
    this.translateService.use(language);
    const user = this.mpsUserService.getUser();
    localStorage.setItem('lang', language);
    if (user) {
      this.mpsUserService.changeLang(user.id, language)?.subscribe();
    }
  }

  getUserDisplayName() {
    return this.user()?.firstname + ' ' + this.user()?.lastname.substring(0, 1) + '.';
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen.set(false);
      this.isUserMenuOpen.set(false);
    }
  }

  impersonateUser(event: { value: User | undefined; label: string }): void {
    if (event.value) {
      this.impersonate(event.value);
    }
  }

  impersonate(user: User): void {
    this.usersService.impersonate(user.id).subscribe({
      next: (token: string) => {
        localStorage.setItem('token', token);
        window.location.reload();
      },
      error: () => {
        this._notificationService.error(this.translateService.instant('messages.api_response.generic_error'));
      },
    });
  }

  isMenuItemDisplayed(route: Route): boolean {
    const user = this.user();
    if (!user) {
      return false;
    }
    const hasTitle = route.data?.['title'];
    const canUserActivate = MpsPermissionHelper.canUserActivate(user, route);

    // Manager EO users can see the administration menu and users/roles child
    if (!canUserActivate && this.managerEoService.isManagerEo()) {
      const isAdminRoute = route.path === applicationRouteNames.administration;
      const isUsersRolesRoute = route.path === applicationRouteNames.users + '/' + applicationRouteNames.usersRoles;
      if (isAdminRoute || isUsersRolesRoute) {
        return !!hasTitle;
      }
    }

    return hasTitle && canUserActivate;
  }

  isChildRouteActive(route: Route): boolean {
    if (!route?.children) {
      return false;
    }
    return route.children.some((child: Route) =>
      this.router.isActive(this.router.createUrlTree([route.path, child.path]), {
        paths: 'subset',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored',
      })
    );
  }

  toggleMobileSubMenu(): void {
    this.isMobileSubMenuOpen.set(!this.isMobileSubMenuOpen());
  }

  /**
   * Returns the translation key for the connected user's role label.
   * Uses existing entities.roles.* keys.
   */
  getUserRoleTranslationKey(): string {
    const role = this.user()?.role;
    if (!role?.code) return '';
    return MpsRoleHelper.getRoleCodeForTranslation(role.code);
  }

  /**
   * Returns the TDS tag accent colour for the connected user's role.
   * Accent values verified against tds-angular TagComponent: 'red' | 'green' | 'blue' | 'yellow' | 'grey' | 'orange' | 'teal' | 'purple' | 'pink'
   */
  getRoleAccent(): 'red' | 'green' | 'blue' | 'yellow' | 'grey' | 'orange' | 'teal' | 'purple' | 'pink' {
    const code = this.user()?.role?.code?.toUpperCase();
    switch (code) {
      case MpsRoleHelper.ADMIN:
        return 'red';
      case MpsRoleHelper.MANAGER:
        return 'blue';
      case MpsRoleHelper.DELEGATED_MANAGER:
        return 'orange';
      default:
        return 'grey';
    }
  }

  /**
   * Returns true when the connected user is a DELEGATED_MANAGER
   * and at least one delegation date is set.
   */
  hasDelegationInfo(): boolean {
    const u = this.user();
    if (!u?.isRole(MpsRoleHelper.DELEGATED_MANAGER)) return false;
    return !!(u.delegation_start || u.delegation_end);
  }

  /**
   * Returns the secondary identity line (IGG and email), skipping whichever is missing
   * so no orphan separator is rendered.
   */
  getUserSecondaryLine(): string {
    const u = this.user();
    return [u?.igg, u?.email].filter(Boolean).join(' · ');
  }

  /**
   * Returns a display-ready list of org entities for the connected user.
   * `code` is the full hierarchical path (TGS/TGITS/MCM) and `name_en` the short leaf
   * name — the leaf is displayed, the full path is exposed as the tooltip.
   * Falls back to org_entity_code (also a path) when entities[] is empty.
   * Returns an empty array when nothing is available.
   */
  getDisplayEntities(): { code: string; name_en: string }[] {
    const u = this.user();
    if (!u) return [];
    if (u.entities && u.entities.length > 0) {
      return u.entities.map((e) => ({ code: e.code, name_en: e.name_en || this.getEntityLeafName(e.code) }));
    }
    if (u.org_entity_code) {
      return [{ code: u.org_entity_code, name_en: this.getEntityLeafName(u.org_entity_code) }];
    }
    return [];
  }

  /**
   * Extracts the short leaf name from a hierarchical org entity path
   * (e.g. 'TGS/TGITS/MCM/DCP/MPS' -> 'MPS').
   */
  private getEntityLeafName(code: string): string {
    const segments = code.split('/').filter((segment) => segment.length > 0);
    return segments.length > 0 ? segments[segments.length - 1] : code;
  }
}
